<template>
  <div
    :class="{
      'weekly-calendar-with-matches': true,
      'weekly-calendar-with-matches--disabled': props.isRequestingDespiteLackingVariety,
    }"
    ref="domRefRoot"
  >
    <!-- Header main -->
    <div class="header-main weekly-calendar-with-matches__header-main">
      <div class="header-main__top-row">
        <div class="header-main__title-row">
          <CalendarPicker :customerId="resolvedCustomerId" suffix="Posts" />
        </div>

        <div class="header-main__actions">
          <div v-if="hasAuthNeededSocialChannel" class="header-main__reauth-wrap">
            <SrpButton color="red" size="small" @click="linkMoreAccounts">
              <template #icon><IconEmbedded name="warning_2-5" :size="20" /></template>
              Re-Auth Needed
            </SrpButton>
          </div>

          <LinkWithIcon class="header-main__settings-link" tag="router-link" isDottedUnderline color="blue" :to="{ name: 'CalendarUploadContent', params: { customerId: resolvedCustomerId } }">
            <template #icon><IconEmbedded name="upload_2" :size="18" /></template>
            Add Photos
          </LinkWithIcon>
          <LinkWithIcon class="header-main__settings-link" tag="router-link" isDottedUnderline color="blue" :to="{ name: 'CalendarConfig', params: { customerId: resolvedCustomerId } }">
            <template #icon><IconEmbedded name="gear_2" :size="18" /></template>
            Config
          </LinkWithIcon>
          <LinkWithIcon class="header-main__settings-link" isDottedUnderline color="blue" @click="openAutoTaggingAnnouncement">
            <template #icon><IconEmbedded name="info_2-5" :size="15" /></template>
            Tagging
          </LinkWithIcon>
        </div>
      </div>

      <div class="header-main__subtitle">Review, adjust, and approve posts to keep your social calendar current!</div>

      <HowContentCalendarWorksSpoiler class="header-main__how-does-it-work-spoiler" />

      <!--<NoteWithIcon color="green">
        <template #text>
          <h4 class="global-h4">Provide Guidance for Social Posts</h4>
          <p class="global-p">Social Posts will be generated from this guidance. You can then approve or adjust the actual social post content.</p>
        </template>
      </NoteWithIcon>-->
    </div>
    <!-- / Header main -->

    <div v-if="isLackingContentVarietyNoticeVisible" class="weekly-calendar-with-matches__variety-notice">
      <NoteWithIcon color="blue">
        <template #text>
          <h3 class="global-h3" style="margin-top: 0">More Content Variety Recommended</h3>
          <p class="global-p" style="margin-bottom: 0">
            You have enough content to generate more posts, but it all comes from one creator visit.<br />Upload more photos to increase the variety of posts, or you can generate posts with what you
            have.
          </p>
          <div class="weekly-calendar-with-matches__variety-notice-actions">
            <SrpButton tag="router-link" :to="{ name: 'CalendarUploadContent', params: { customerId: resolvedCustomerId } }">
              <template #icon><IconEmbedded name="upload_2" :size="22" /></template>
              Upload Photos
            </SrpButton>
            <SrpButton :isDisabled="props.isRequestingDespiteLackingVariety" fill="outlined" @click="emit('requestGenerateDespiteLackingVariety')">
              <template #icon>
                <LoadingIcon v-if="props.isRequestingDespiteLackingVariety" :size="22" />
                <IconEmbedded v-else name="magic-wand_2" :size="22" />
              </template>
              Generate Posts Anyway
            </SrpButton>
          </div>
        </template>
      </NoteWithIcon>
    </div>

    <div v-if="props.isLoading || isGenerating" style="margin-bottom: 35px; display: flex; flex-direction: column; align-items: center; text-align: center">
      <LoaderNew style="width: 70px; height: 70px; margin-bottom: 10px" :borderWidth="3" />
      <div class="global-p">
        <h2>{{ getGenerationTitle() }}</h2>
        <span v-if="isGenerating"><span v-html="getGenerationSubtext()"></span><EllipsisAnimated /></span>
      </div>
    </div>

    <template v-else>
      <template
        v-for="primaryGroup in [
          {
            name: CalendarPrimaryGroupName.Past,
            weeks: props.matchedResults?.[CalendarPrimaryGroupName.Past].weeks,
          },
          {
            name: CalendarPrimaryGroupName.Upcoming,
            weeks: upcomingWeeksWithMinimum,
          },
        ]"
        :key="primaryGroup.name"
      >
        <div
          v-if="primaryGroup.name !== CalendarPrimaryGroupName.Past || isPreviousPostsSectionVisible"
          :class="{
            'weekly-calendar-with-matches__week-group-list-wrap': true,

            'weekly-calendar-with-matches__week-group-list-wrap--no-frame': ![CalendarPrimaryGroupName.Past, CalendarPrimaryGroupName.Upcoming].includes(primaryGroup.name),

            'weekly-calendar-with-matches__week-group-list-wrap--gray-frame': primaryGroup.name === CalendarPrimaryGroupName.Past,
            'weekly-calendar-with-matches__week-group-list-wrap--teal-frame': primaryGroup.name === CalendarPrimaryGroupName.Upcoming,
            'weekly-calendar-with-matches__week-group-list-wrap--has-create-posts':
              primaryGroup.name === CalendarPrimaryGroupName.Upcoming && props.matchedResults?.enableCreateManyPosts,
          }"
        >
          <div class="weekly-calendar-with-matches__section-header">
            <div class="global-h2 weekly-calendar-with-matches__section-title" :data-section-title="primaryGroup.name === CalendarPrimaryGroupName.Upcoming ? 'upcoming-posts' : undefined">
              {{
                {
                  [CalendarPrimaryGroupName.Past]: "Previous Posts",
                  [CalendarPrimaryGroupName.Upcoming]: "Upcoming Posts",
                }[primaryGroup.name]
              }}
            </div>
            <span
              v-if="primaryGroup.name === CalendarPrimaryGroupName.Upcoming && !isPreviousPostsSectionVisible && shouldShowOlderPostsLink"
              class="weekly-calendar-with-matches__show-older"
            >
              <LinkWithIcon v-if="!props.isLoadingOlderPosts" isDottedUnderline color="gray" @click="handleShowOlderPostsClick" style="display: inline-flex">
                <template #icon><IconEmbedded name="clock_2-5" :size="13" /></template>
                Show Older
              </LinkWithIcon>
              <template v-else>
                <LoadingIcon :size="14" />
                <span class="global-p" style="margin: 0; opacity: 0.8">Loading older posts…</span>
              </template>
            </span>
            <div
              v-if="primaryGroup.name === CalendarPrimaryGroupName.Upcoming && props.matchedResults?.enableCreateManyPosts"
              class="weekly-calendar-with-matches__create-posts-wrap"
            >
              <SrpButton size="small" @click="openTopLevelCreatePosts">
                <template #icon><IconEmbedded name="copy_2" :size="20" /></template>
                Create Posts
              </SrpButton>
            </div>
          </div>
          <ul v-if="primaryGroup.name !== CalendarPrimaryGroupName.Past || isPreviousPostsSectionVisible" class="weekly-calendar-with-matches__week-group-list">
            <li
              v-if="primaryGroup.name === CalendarPrimaryGroupName.Past && !showOlderPublished && (primaryGroup.weeks?.length || 0) > DEFAULT_PREVIOUS_WEEKS_TO_SHOW"
              class="week-group__calendar-item-container week-group__calendar-item-container--centered"
              style="margin-bottom: 0; opacity: 0.75"
            >
              <SrpButton size="small" color="gray" fill="outlined" @click="showOlderPublished = true">Show Older</SrpButton>
            </li>
            <!-- Week group -->
            <li
              :class="{
                'week-group': true,
                'weekly-calendar-with-matches__week-group': true,
                'weekly-calendar-with-matches__week-group--smaller-vertical-margins': !weekGroup.items?.length,
              }"
              v-for="weekGroup in getVisibleWeeks(primaryGroup)"
              :key="weekGroup.weekGroup"
              ref="domRefWeekGroupArray"
              :data-id="weekGroup.weekGroup"
              data-type="week"
              :data-week="weekGroup.weekGroup"
              :data-primary-group="primaryGroup.name"
            >
              <!-- Week title divider -->
              <div class="week-title-divider week-group__title">
                <h5 class="global-h5 week-title-divider__text" :data-week-title="weekGroup.weekGroup">{{ DayjsExtended(weekGroup.weekGroup).format("MMMM Do") }} week</h5>
              </div>
              <!-- / Week title divider -->

              <!-- Future Week options (only for weeks >= nextPostGenForWeek) -->
              <WeeklyGuidanceSection
                v-if="shouldShowWeekGuidance(weekGroup.weekGroup) || isDebugMode"
                :weekKey="weekGroup.weekGroup"
                :modelValue="getGuidanceDisplay(weekGroup)"
                :systemTopics="weekGroup.systemTopics"
                :isExpanded="expandedGuidanceWeekKey === weekGroup.weekGroup"
                :isSaving="savingWeekKeys.has(weekGroup.weekGroup)"
                :showDebugAction="isDebugMode"
                :isDebugActionLoading="props.debugPostGenLoadingWeekKey === weekGroup.weekGroup"
                @open="openWeekGuidanceEditor(weekGroup.weekGroup)"
                @update:modelValue="onGuidanceInput(weekGroup, $event)"
                @blur="
                  () => {
                    saveWeekGuidance(weekGroup, primaryGroup);
                    expandedGuidanceWeekKey = null;
                  }
                "
                @triggerPostGen="emit('triggerWeekPostGen', $event)"
              />
              <!-- / Future Week options -->

              <ul class="week-group__calendar-items-list">
                <!-- Calendar item container -->
                <template v-for="(bucket, index) in sortItemsByPostAndOrder(weekGroup.items)" :key="bucket.calendarItem.id">
                  <li
                    v-if="shouldShowCalendarItemBucket(bucket)"
                    :class="{
                      'calendar-item-container': true,
                      'week-group__calendar-item-container': true,
                    }"
                  >
                    <!-- calendarItem.id is user fro the "data-id" because unlike calendarItem, it is guaranteed to be present in the bucket, unlike post and contentMatch -->
                    <div
                      class="calendar-item-container__in-wrap"
                      ref="domRefCalendarItemContainerArray"
                      :data-index="index"
                      data-type="bucket"
                      :data-week="weekGroup.weekGroup"
                      :data-id="bucket.calendarItem.id"
                      :data-primary-group="primaryGroup.name"
                      :data-has-post="Boolean(bucket.socialPost?.id)"
                    >
                      <!-- Social post container -->
                      <div
                        v-if="postIdsThatVisible.includes(bucket.socialPost?.id) || [CalendarItemGenStatus.Pending, CalendarItemGenStatus.Generating].includes(bucket.calendarItem.genStatus)"
                        class="social-post-container calendar-item-container__social-post-container"
                      >
                        <div v-if="[CalendarItemGenStatus.Pending, CalendarItemGenStatus.Generating].includes(bucket.calendarItem.genStatus)" class="social-post-container__skeleton-wrap">
                          <div class="social-post-container__skeleton-overlay-text">Generating the post...<br />This can take a minute</div>
                          <SocialPostSkeleton class="social-post-container__skeleton" />
                        </div>

                        <SocialPostPreview
                          v-else-if="bucket.socialPost"
                          class="calendar-item-container__post"
                          :isUnusedPost="isPostUnused(bucket.socialPost)"
                          :post="bucket.socialPost"
                          :isStatusAndSocialsPanelVisible="true"
                          :isPostScheduleVisible="true"
                          :isLocationEditVisible="canEditPostFromPreview(bucket.socialPost)"
                          :isLocationEditDisabled="postIdsThatBeingUpdated.includes(bucket.socialPost.id)"
                          :showDebug="isDebugMode"
                          :showEvals="areEvalsVisible"
                          :creationFlow="bucket.calendarItem.creationFlow"
                          :debugData="findDebugInfo(bucket.socialPost.id)"
                          @editPost="openPostEditor(bucket)"
                          @updatePostSchedule="
                            response => {
                              MetricSender.sendMetric('Cust-Calendar-Post-UpdateSchedule', { socialPostId: bucket.socialPost.id });
                              emit('postsUpdated', [response.post], response.newWeekKey);
                            }
                          "
                          @updatePostToAccounts="
                            selectedSocialChannels => {
                              MetricSender.sendMetric('Cust-Calendar-Post-SetPostTo', { socialPostId: bucket.socialPost.id, postToAccounts: selectedSocialChannels });
                              emit('postsUpdated', [{ ...bucket.socialPost, postToAccounts: selectedSocialChannels }]);
                            }
                          "
                          @addSocialAccount="linkMoreAccounts"
                          :customerSocialChannels="customersSocialChannelsForPicker"
                        >
                          <template #footer>
                            <NoteWithIcon v-if="postIdsToShowScheduledDateWarning.includes(bucket.socialPost.id)" size="tiny" color="red" style="margin-bottom: 10px">
                              <template #text
                                ><b>Must be scheduled at least {{ POST_APPROVAL_MINUTES_AHEAD }} minutes in the future</b></template
                              >
                            </NoteWithIcon>

                            <div v-if="!bucket.socialPost.postedDetails?.length" style="gap: 9px; margin-bottom: 17px; display: flex; align-items: center">
                              <InfoIconWithTooltip v-if="tooLateToApprovePost(bucket.socialPost)" style="position: relative; top: 5px">
                                <b>To use this post, set the date below to the future.</b>
                              </InfoIconWithTooltip>

                              <template v-if="!tooLateToApprovePost(bucket.socialPost)">
                                <SrpButton
                                  v-if="isPostEligibleForApproval(bucket.socialPost)"
                                  size="tiny"
                                  @click="approvePost(bucket.socialPost)"
                                  :isDisabled="
                                    postIdsThatBeingUpdated.includes(bucket.socialPost.id) ||
                                    postIdsToShowScheduledDateWarning.includes(bucket.socialPost.id) ||
                                    tooLateToApprovePost(bucket.socialPost)
                                  "
                                  style="width: 100px"
                                >
                                  <template #icon>
                                    <LoadingIcon v-if="postIdsThatBeingUpdated.includes(bucket.socialPost.id)" :size="15" />
                                    <IconEmbedded v-else name="check_3-5" :size="15" />
                                  </template>
                                  Approve
                                </SrpButton>

                                <SrpButton
                                  v-if="bucket.socialPost.status === 'Approved'"
                                  size="tiny"
                                  @click="unapprovePost(bucket.socialPost.id)"
                                  fill="outlined"
                                  :isDisabled="postIdsThatBeingUpdated.includes(bucket.socialPost.id)"
                                  style="width: 110px"
                                >
                                  <template #icon>
                                    <LoadingIcon v-if="postIdsThatBeingUpdated.includes(bucket.socialPost.id)" :size="15" />
                                    <IconEmbedded v-else name="check_3-5" :size="15" />
                                  </template>
                                  Unapprove
                                </SrpButton>

                                <SrpButton
                                  size="tiny"
                                  color="gray"
                                  fill="outlined"
                                  @click="openPostEditor(bucket)"
                                  :isDisabled="postIdsThatBeingUpdated.includes(bucket.socialPost.id)"
                                  style="width: 68px"
                                >
                                  <template #icon><IconEmbedded name="pencil_2" :size="15" /></template>
                                  Edit
                                </SrpButton>
                              </template>

                              <div style="flex-grow: 1"></div>

                              <SocialPostRating
                                v-if="!isPostUnused(bucket.socialPost)"
                                :rating="bucket.socialPost.rating ?? 0"
                                :isApproved="bucket.socialPost.status === SocialPostStatus.Approved"
                                @rate="rating => handleRatePost(bucket.socialPost.id, rating)"
                                @approve="approvePost(bucket.socialPost)"
                                @remove="
                                  () => {
                                    bucketToDislike = bucket;
                                    isPostDislikeModalVisible = true;
                                  }
                                "
                              />

                              <SrpButton
                                v-if="bucket.socialPost.status !== SocialPostStatus.Approved && isPostUnused(bucket.socialPost)"
                                style="width: 15px !important; min-width: 15px !important; max-width: 15px !important; display: flex"
                                size="small"
                                color="gray"
                                fill="outlined"
                                @click="
                                  () => {
                                    bucketToDislike = bucket;
                                    isPostDislikeModalVisible = true;
                                  }
                                "
                              >
                                <template #icon><IconEmbedded name="dislike_2" :size="21" /></template>
                              </SrpButton>
                            </div>
                          </template>
                        </SocialPostPreview>
                      </div>
                      <!-- / Social post container -->

                      <div v-else class="calendar-item-container__calendar-item-wrap" :data-week="weekGroup.weekGroup" :data-dragId="bucket.calendarItem.id" :data-primary-group="primaryGroup.name">
                        <CalendarItem
                          class="calendar-item-container__calendar-item"
                          :calendarItem="bucket.calendarItem"
                          :isActionButtonsVisible="!bucket.socialPost"
                          :showDebug="isDebugMode"
                          @delete="
                            () => {
                              itemToDeleteId = bucket.calendarItem.id;
                              itemToDeleteTitle = bucket.calendarItem.title;
                              isConfirmItemDelectionModalVisible = true;
                              MetricSender.sendMetric('Cust-Calendar-Topic-Delete', { calendarItemId: bucket.calendarItem.id });
                            }
                          "
                        />

                        <div v-if="bucket.isLoadingContentMatches" class="calendar-item-container__content-match-snippet" style="display: flex; align-items: center; gap: 8px; margin-top: 10px">
                          <LoadingIcon :size="18" />
                          <span class="global-p" style="margin: 0">Loading inputs...</span>
                        </div>
                        <template v-else-if="getContentMatches(bucket).length">
                          <ContentSummary
                            class="calendar-item-container__content-match-snippet"
                            :contentSummaryDataList="getContentMatches(bucket)"
                            :matchRationale="bucket.calendarItem.matchRationale"
                            @clickViewPhotos="
                              contentSummary => {
                                if (!contentSummary) return;
                                viewAllPhotosSummaryId = contentSummary.id;
                                isViewAllPhotosModalVisible = true;
                                MetricSender.sendMetric('Cust-Calendar-Topic-ViewPhotos', { calendarItemId: bucket.calendarItem.id });
                              }
                            "
                            :isUnbindButtonVisible="primaryGroup.name === CalendarPrimaryGroupName.Upcoming && !bucket.socialPost"
                            :customerId="props.customerId"
                          />
                        </template>
                        <ContentSummaryEmpty
                          v-else
                          class="calendar-item-container__content-match-snippet"
                          :hasPost="Boolean(bucket.socialPost)"
                          :noAlbumMatch="!bucket.isLoadingContentMatches && showsMissingContentEmptyState(bucket)"
                          :preferReviewOptionsLink="missingContentReviewOptionsItemId === bucket.calendarItem.id"
                          @clickPickFromExisting="
                            () => {
                              pickFromExistingContentItem = bucket.calendarItem;
                              isPickFromExistingContentModalVisible = true;
                              MetricSender.sendMetric('Cust-Calendar-Topic-PickContentFromExisting', { calendarItemId: bucket.calendarItem.id });
                            }
                          "
                          @clickReviewOptions="
                            () => {
                              openReviewOptionsFromBucket(bucket, primaryGroup.name);
                              MetricSender.sendMetric('Cust-Calendar-Topic-MissingContentReviewOptions', { calendarItemId: bucket.calendarItem.id });
                            }
                          "
                          @clickUploadContent="
                            () => {
                              isContentUploadModalVisible = true;
                              calendarItemToUploadContentFor = bucket.calendarItem;
                              MetricSender.sendMetric('Cust-Calendar-Topic-UploadContent', { calendarItemId: bucket.calendarItem.id });
                            }
                          "
                        />
                      </div>

                      <div v-if="bucket.socialPost" class="calendar-item-container__social-post-switch-row">
                        <LinkWithIcon class="calendar-item-container__social-post-switch" isDottedUnderline iconInset="1px -2px auto auto" @click="togglePostVisibility(bucket)" color="light-gray">
                          <template #icon>
                            <IconEmbedded v-if="postIdsThatVisible.includes(bucket.socialPost?.id)" name="notepad_2" :size="17" />
                            <IconEmbedded v-else name="arrow-corner-top-left_2-5" :size="17" />
                          </template>
                          <span v-if="postIdsThatVisible.includes(bucket.socialPost?.id)">Guidance</span>
                          <span v-else>Social Post</span>
                        </LinkWithIcon>

                        <IconEmbedded
                          v-if="isDebugMode"
                          @click.stop="openConfigAdminForPost(bucket.socialPost.id)"
                          name="external-link_2"
                          :size="17"
                          color="rgba(0, 0, 0, 0.3)"
                          style="margin: 0 4px 0 3px; position: relative; top: 2px"
                        />
                      </div>
                    </div>
                  </li>
                </template>
                <!-- / Calendar item container -->

                <li
                  v-if="primaryGroup.name !== CalendarPrimaryGroupName.Past"
                  :class="{
                    'week-group__calendar-item-container': true,
                    'week-group__calendar-item-container--centered': weekGroup.items?.length > 0,
                    'week-group__calendar-item-container--left-aligned': !weekGroup.items?.length,
                  }"
                >
                  <CalendarCreateContentMenu @selectPostType="postType => openNewPostModal(primaryGroup.name, weekGroup.weekGroup, postType)" />
                </li>
                <!-- / Add Post item container -->
              </ul>
            </li>
            <!-- / Week group -->

            <li
              v-if="primaryGroup.name === CalendarPrimaryGroupName.Upcoming && hasHiddenFutureWeeks"
              class="week-group__calendar-item-container week-group__calendar-item-container--centered"
              style="margin-top: 15px; margin-bottom: 0; opacity: 0.75"
            >
              <SrpButton size="small" color="gray" fill="outlined" @click="showFutureWeeks = true">Show More</SrpButton>
            </li>

            <template v-if="primaryGroup.name === CalendarPrimaryGroupName.Upcoming && (isSomeScheduledPostsEligibleForApproval || (props.processingSummaries?.length ?? 0) > 0)">
              <div class="weekly-calendar-with-matches__divider"></div>
              <!-- Manage buttons floating panel -->
              <div class="manage-buttons-floating-panel weekly-calendar-with-matches__manage-buttons-floating-panel">
                <div
                  v-if="isAllPostsScheduleWarningVisible"
                  style="width: 250px; position: absolute; inset: -43px auto auto 0; color: #945347; line-height: 120%; box-shadow: 0 0 20px 20px rgba(255, 255, 255, 1); background: white"
                >
                  <b
                    >All posts must be scheduled at<br />
                    least {{ POST_APPROVAL_MINUTES_AHEAD }} minutes in the future</b
                  >
                </div>
                <SrpButton
                  v-if="isSomeScheduledPostsEligibleForApproval"
                  class="manage-buttons-floating-panel__button"
                  :isDisabled="isApprovingAllPosts || props.isLoading || isAllPostsScheduleWarningVisible"
                  color="orange"
                  size="small"
                  @click="approveAllPosts"
                >
                  <template #icon>
                    <LoadingIcon v-if="isApprovingAllPosts" :size="20" />
                    <IconEmbedded v-else name="check_3-5" :size="20" />
                  </template>
                  Approve all
                </SrpButton>
                <ContentSummaryProcessingIndicator :items="props.processingSummaries ?? []" class="manage-buttons-floating-panel__processing-indicator" />
              </div>
              <!-- / Manage buttons floating panel -->
            </template>
          </ul>

          <div class="weekly-calendar-with-matches__sticky-bottom-border"></div>
        </div>
      </template>

      <FutureContentNeeds
        v-if="props.futureCalendarSummary?.nextCalendarGenSummary || props.futureCalendarSummary?.longTermCalendarGenSummary"
        :nextCalendarGenSummary="props.futureCalendarSummary?.nextCalendarGenSummary"
        :longTermCalendarGenSummary="props.futureCalendarSummary?.longTermCalendarGenSummary"
        :customerId="props.customerId"
        :nextPostGenOnWeek="props.matchedResults?.nextPostGenOnWeek"
        :showDebug="isDebugMode"
        class="weekly-calendar-with-matches__future-content-needs"
      />
    </template>

    <DislikePostConfirmModal v-if="isPostDislikeModalVisible" v-model:isVisible="isPostDislikeModalVisible" :isDisabled="isRemovingPost">
      <template #footer="{ closeModal, combinedFeedbackString }">
        <SrpButton
          @click="
            async () => {
              await dislikeAndRemovePost(combinedFeedbackString, props.customerId, bucketToDislike!.socialPost!.id);
              bucketToDislike = null;
              closeModal();
            }
          "
          style="margin-right: -8px"
          :isDisabled="isRemovingPost"
        >
          <template #icon>
            <LoadingIcon v-if="isRemovingPost" :size="22" />
            <IconEmbedded v-else name="trashcan-alt_2" :size="22" />
          </template>
          Remove Post
        </SrpButton>
      </template>
    </DislikePostConfirmModal>

    <SocialPostEditAndShareModal
      v-if="isPostEditModalVisible"
      v-model:isVisible="isPostEditModalVisible"
      :postToEdit="bucketToEdit?.socialPost"
      :sourceEntityName="formatContentSourceName(bucketToEdit)"
      :customerId="props.customerId"
      :customerSocialChannels="customersSocialChannelsForPicker"
      :sectionsToHide="[SocialPostEditAndShareFormSections.CreatorTag]"
      :calendarImageFit="calendarImageFit"
      @savePost="handlePostEditSave"
      @addSocialAccount="linkMoreAccounts"
    />

    <PickFromExistingContentModal
      v-if="isPickFromExistingContentModalVisible"
      v-model:isVisible="isPickFromExistingContentModalVisible"
      :customerId="props.customerId"
      :calendarItem="pickFromExistingContentItem"
      @close="pickFromExistingContentItem = null"
      @picked="contentSummary => handleContentSummaryBoundToItem(pickFromExistingContentItem, contentSummary)"
    />

    <DeleteItemConfirmModal
      v-if="isConfirmItemDelectionModalVisible"
      v-model:isVisible="isConfirmItemDelectionModalVisible"
      :customerId="props.customerId"
      :itemId="itemToDeleteId"
      :itemTitle="itemToDeleteTitle"
      @delete="emit('bucketDeleted', ContentCalendarHelper.findBucketPath(props.matchedResults, itemToDeleteId))"
    />

    <MissingContentMatchModal
      v-if="missingContentModalContext"
      v-model:isVisible="isMissingContentFromStatusModalVisible"
      :customerId="props.customerId"
      @adjustTheme="onMissingContentAdjustTheme"
      @manualDraft="onMissingContentManualDraft"
    />

    <AutoPostTaggingAnnouncementModal ref="autoPostTaggingAnnouncementModalRef" :customerId="resolvedCustomerId" />

    <NewSocialAccountAddedModal
      :customerId="resolvedCustomerId"
      :pendingChannels="systemSettings?.pendingChannels"
      :socialChannels="customersSocialChannelsForPicker"
      @applied="handlePendingChannelsApplied"
    />

    <NewPostModal
      v-if="isNewPostModalVisible"
      :isVisible="isNewPostModalVisible"
      :customerId="props.customerId"
      :initialMode="newPostInitialMode"
      :initialTitle="newPostInitialTitle"
      :initialDescription="newPostInitialDescription"
      :initialCaption="newPostInitialCaption"
      :initialPostType="newPostInitialPostType"
      :initialCreateManyPosts="newPostInitialCreateManyPosts"
      :customerTimeZone="customerTimeZone"
      :selectedChannels="connectedPlatformChannelTypes"
      :calendarImageFit="calendarImageFit"
      :enableCreateManyPosts="props.matchedResults?.enableCreateManyPosts"
      :maxCreateManyPosts="props.matchedResults?.maxCreateManyPosts"
      @update:isVisible="
        isVisible => {
          isNewPostModalVisible = isVisible;
          if (!isVisible) {
            primaryGroupNewCalendarItemToAddTo = null;
            weekGroupNewCalendarItemToAddTo = null;
            newPostInitialMode = null;
            newPostInitialTitle = null;
            newPostInitialDescription = null;
            newPostInitialCaption = null;
            newPostInitialPostType = null;
            newPostInitialCreateManyPosts = false;
          }
        }
      "
      @createCalendarItems="handleCalendarItemsCreated"
      @createNewItemAndPost="newItemAndPost => emit('manualPostCreated', primaryGroupNewCalendarItemToAddTo, newItemAndPost.calendarItem.weekGroup, newItemAndPost)"
      :weekGroup="weekGroupNewCalendarItemToAddTo"
    />

    <!--<div class="weekly-calendar-with-matches__divider"></div>-->

    <!-- Manage buttons floating panel -->
    <!--<div class="manage-buttons-floating-panel weekly-calendar-with-matches__manage-buttons-floating-panel weekly-calendar-with-matches__manage-buttons-floating-panel--right-aligned">
      <SrpButton
        class="manage-buttons-floating-panel__button"
        color="gray"
        fill="outlined"
        @click="emit('requestGoBack')"
        :isDisabled="props.isLoading"
        iconInset="0 auto auto auto"
        :size="screenSize === ScreenSizesEnum.Mobile ? 'small' : 'normal'"
        :isVertical="[ScreenSizesEnum.Mobile, ScreenSizesEnum.Tablet].includes(screenSize)"
      >
        <template #icon><IconEmbedded name="arrow-left_3-5" :size="17" /></template>
        <template v-if="[ScreenSizesEnum.Mobile, ScreenSizesEnum.Tablet].includes(screenSize)">Go<br />back</template>
        <template v-else>Go back</template>
      </SrpButton>
    </div>-->
    <!-- / Manage buttons floating panel -->

    <!-- Super user action panel at the bottom -->
    <div style="margin-top: 50px; display: flex; justify-content: flex-end; width: 100%">
      <SuperUserActionPanel>
        <div>
          <SrpButton color="gray" fill="outlined" size="tiny" @click="toggleDebugInfo"> {{ isDebugMode ? "Hide" : "Show" }} Debug UI</SrpButton>
          <SrpButton color="gray" fill="outlined" size="tiny" style="margin-left: 8px" @click="areEvalsVisible = !areEvalsVisible"> {{ areEvalsVisible ? "Hide" : "Show" }} Evals </SrpButton>
          <SrpButton color="gray" fill="outlined" size="tiny" style="margin-left: 8px" @click="isFutureCalendarTestVisible = true"> Future Cal Test </SrpButton>
          <SrpButton color="gray" fill="outlined" size="tiny" style="margin-left: 8px" @click="toggleShowTestCalendars"> {{ showTestCalendars ? "Hide" : "Show" }} Test Cal </SrpButton>
        </div>
        <div style="margin-top: 8px; gap: 8px; display: flex; flex-direction: row; align-items: center">
          <SrpButton color="gray" fill="outlined" size="tiny" @click="emit('showDeleted')"> {{ props.includeDeleted ? "Hide" : "Show" }} Deleted </SrpButton>
          <SrpButton color="gray" fill="outlined" size="tiny" @click="openCalendarGenPrompt"> Cal Gen Prompt </SrpButton>
          <SrpButton color="gray" fill="outlined" size="tiny" @click="openCalendarMatchPrompt"> Match Prompt </SrpButton>
          <SrpButton color="gray" fill="outlined" size="tiny" style="margin-left: 8px" @click="openSuperuserTestNonMatchModal"> Test non-match </SrpButton>
        </div>
        <div v-if="props.matchedResults" style="margin: 4px">
          {{ props.matchedResults.topicFilterCount }} filtered topics
          <LinkWithIcon v-if="props.matchedResults.topicFilterCount > 0" isDottedUnderline color="gray" @click="emit('showFilteredTopics')" style="margin-left: 6px"> show </LinkWithIcon>
          <div style="margin-top: 4px">
            <LinkWithIcon v-if="!inputInfo" isDottedUnderline color="gray" :isDisabled="isLoadingInputInfo" @click="loadInputInfo"> Show content summary stats </LinkWithIcon>
            <div v-if="isLoadingInputInfo" style="display: flex; align-items: center; gap: 6px; margin-top: 4px">
              <LoadingIcon :size="14" />
              <span class="global-p" style="margin: 0">Loading Content Summary stats...</span>
            </div>
            <div v-else-if="inputInfoLoadError" class="global-p" style="margin: 4px 0 0">{{ inputInfoLoadError }}</div>
            <div v-else-if="inputInfo" style="margin-top: 4px">
              <div style="font-weight: 500">{{ inputInfo.contentSummaryCount }} Content Groups:</div>
              <div v-if="inputInfo.contentSummaryByType && Object.keys(inputInfo.contentSummaryByType).length">
                <span v-for="(count, type) in inputInfo.contentSummaryByType" :key="type" style="margin-left: 8px"> {{ count }} {{ type }},</span>
              </div>
            </div>
          </div>
        </div>
      </SuperUserActionPanel>
    </div>

    <FutureCalendarTest v-if="isFutureCalendarTestVisible" :customerId="props.customerId" />

    <UploadContentForItemModal
      v-if="isContentUploadModalVisible"
      v-model:isVisible="isContentUploadModalVisible"
      :customerId="props.customerId"
      :calendarItemTitle="calendarItemToUploadContentFor.title"
    />

    <ViewAllPhotosModal v-if="isViewAllPhotosModalVisible" v-model:isVisible="isViewAllPhotosModalVisible" :customerId="props.customerId" :contentSummaryId="viewAllPhotosSummaryId" />

    <SocialAccountConnectPrep
      v-model:isVisible="isSocialAccountConnectPrepVisible"
      :customerId="props.customerId"
      :isRedirecting="isRedirectingToPortal"
      :connectedPlatforms="connectedPlatformChannelTypes"
      @confirm="confirmLinkMoreAccounts"
    />

    <PickSocialNetworksModal
      v-if="postForApprovalNetworkPicker"
      v-model:isVisible="isApprovalNetworkPickerVisible"
      :allCustomerSocialChannels="customersSocialChannelsForPicker"
      :selectedSocialChannels="postForApprovalNetworkPicker.postToAccounts"
      :post="postForApprovalNetworkPicker"
      @save="handleApprovalNetworkPickerSave"
      @addSocialAccount="linkMoreAccounts"
    />

    <SrpModal v-model:isVisible="isApproveAllSelectAccountsModalVisible" size="small">
      <template #header>
        <h2 class="global-h2">Select Accounts</h2>
      </template>
      <template #content>
        <p class="global-p">Some posts don't have accounts selected. Choose accounts on each post before approving all.</p>
      </template>
      <template #footer>
        <SrpButton @click="isApproveAllSelectAccountsModalVisible = false">OK</SrpButton>
      </template>
    </SrpModal>

    <SrpModal v-model:isVisible="isTeamMemberApprovalBlockedModalVisible" size="small">
      <template #header>
        <h2 class="global-h2">Approval Restricted</h2>
      </template>
      <template #content>
        <p class="global-p">Shrpa team members cannot approve social posts for customer accounts.<br />Please have the customer approve their posts directly.</p>
      </template>
      <template #footer>
        <SrpButton @click="isTeamMemberApprovalBlockedModalVisible = false">OK</SrpButton>
      </template>
    </SrpModal>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from "vue";
import DayjsExtended from "@logic/DayjsExtended";
import { isChannelAuthNeeded, isPostEligibleForApproval } from "@logic/SocialMediaPost";
import ContentCalendarHelper, { compareCalendarItemsBySchedule, MatchedResultObjBucketPath } from "@logic/ContentCalendarHelper";
import { isScheduledDateTooSoon, POST_APPROVAL_MINUTES_AHEAD, MS_TO_SHOW_WARNING } from "@logic/ContentCalendarValidator";
import { MetricSender } from "@helpers/MetricSender";
import { getGlobalRemoteLogger } from "@helpers/RemoteLogger";
// import { useDragNDrop } from "@composables/useDragNDrop";

// Repos
import ContentCalendarRepo from "@repos/ContentCalendarRepo";
import SocialMediaSettingsRepo from "@repos/SocialMediaSettingsRepo";
import SocialMediaApproveAndEditRepo from "@repos/SocialMediaApproveAndEditRepo";
import ShareStopSummaryRepo from "@repos/ShareStopSummaryRepo";

// Types
import { SocialPostEditAndShareFormSections } from "@components/SocialPostEditAndShareModal/index.vue";

// Components
import SrpButton from "@components/ui/SrpButton.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LoaderNew from "@components/Loader/LoaderNew.vue";
import EllipsisAnimated from "@components/ui/EllipsisAnimated.vue";
import SocialPostPreview from "@components/SocialPostPreview/index.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import CalendarPicker from "@components/CalendarPicker.vue";
import HowContentCalendarWorksSpoiler from "@views/Community/ContentCalendar/HowContentCalendarWorksSpoiler.vue";
import SocialPostSkeleton from "@components/SocialPostPreview/SocialPostSkeleton.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import SuperUserActionPanel from "@components/SuperUserActionPanel.vue";
import InfoIconWithTooltip from "@components/ui/InfoIconWithTooltip.vue";
import SocialPostRating from "@components/SocialPostPreview/SocialPostRating.vue";
// modals
import NewPostModal from "./modals/NewPostModal.vue";
import MissingContentMatchModal from "./modals/MissingContentMatchModal.vue";
import DeleteItemConfirmModal from "./modals/DeleteItemConfirmModal.vue";
import PickFromExistingContentModal from "./modals/PickFromExistingContentModal.vue";
import UploadContentForItemModal from "./modals/UploadContentForItemModal.vue";
import SocialPostEditAndShareModal from "@components/SocialPostEditAndShareModal/index.vue";
import ViewAllPhotosModal from "./modals/ViewAllPhotosModal.vue";
import SrpModal from "@components/ui/SrpModal.vue";
import FutureCalendarTest from "./FutureCalendarTest.vue";
// snippets
import CalendarItem from "../eventSnippets/CalendarItem.vue";
import ContentSummary from "../eventSnippets/ContentSummary.vue";
import ContentSummaryEmpty from "../eventSnippets/ContentSummaryEmpty.vue";
import DislikePostConfirmModal from "@components/SocialPostPreview/DislikePostConfirmModal.vue";
import FutureContentNeeds from "../FutureContentNeeds.vue";
import ContentSummaryProcessingIndicator from "./ContentSummaryProcessingIndicator.vue";
import WeeklyGuidanceSection from "./WeeklyGuidanceSection.vue";
import SocialAccountConnectPrep from "../SocialAccountConnectPrep.vue";
import PickSocialNetworksModal from "@components/SocialPostPreview/PickSocialNetworksModal.vue";
import CalendarCreateContentMenu from "./CalendarCreateContentMenu.vue";
import AutoPostTaggingAnnouncementModal from "./modals/AutoPostTaggingAnnouncementModal.vue";
import NewSocialAccountAddedModal from "./modals/NewSocialAccountAddedModal.vue";

// Composables

// Types
import {
  CalendarInputInfo,
  CalendarItemGenStatus,
  CalendarPrimaryGroupName,
  ContentCalendarItem,
  ContentCalendarItemMatch,
  ContentCalendarMatchedResults,
  ContentCalendarWeekMatch,
  ContentSummaryPreview,
  ContentSummaryProcessingItem,
  NextFutureCalendarResult,
  SocialOperationType,
  SocialPostType,
} from "@contracts/contentCalendar";
import { CalendarGenerationStatus } from "../types";
import { ImageSelectAIDebugData, SocialChannelStatus, SocialMediaPostData, SocialPostStatus } from "@contracts/shareStop";
import type { ContentCalendarSystemSettingsData, ContentCalendarUserSettingsData, ApplyPendingChannelsResponse } from "@contracts/socialMediaSettings";
import type { SocialImageFitSettings } from "@contracts/shareStop";
import type { SocialChannels } from "@contracts/socialNetworks";

// Router
import { useRoute, useRouter } from "vue-router";
import NoteWithIcon from "@components/NoteWithIcon.vue";
import { getCalendarId, getShowTestCalendars, setShowTestCalendars } from "@/logic/calendarManager";

// Stores
import { useUserProfileStore } from "@stores/userProfileStore";

// Global variables & injections
// const screenSize = inject("screenSize") as Ref<ScreenSize>;

const route = useRoute();
const router = useRouter();
const userProfileStore = useUserProfileStore();
const resolvedCustomerId = computed<string>(() => (Array.isArray(route.params.customerId) ? route.params.customerId[0] : (route.params.customerId as string)));

const remoteLogger = getGlobalRemoteLogger();

// Constants for team member approval blocking
const ALLOWED_TEST_CUSTOMER_IDS = ["funtown", "shrpautility"];
const TEAM_APPROVE_OVERRIDE_STORAGE_KEY = "shrpa-allow-team-approve";

// Helper function to check if team member approval should be blocked
function shouldBlockTeamMemberApproval(): boolean {
  // Hidden override: if localStorage has the special key, skip this check (any environment)
  if (localStorage.getItem(TEAM_APPROVE_OVERRIDE_STORAGE_KEY)) {
    return false;
  }

  const actingProfile = userProfileStore.getActingUserProfile;
  if (!actingProfile?.isSuperuser && !actingProfile?.isSalesUser) {
    return false;
  }

  // Allow for test customers
  if (ALLOWED_TEST_CUSTOMER_IDS.includes(props.customerId?.toLowerCase())) {
    return false;
  }

  return true;
}

const props = withDefaults(
  defineProps<{
    flatBucketList: Array<ContentCalendarItemMatch>;
    matchedResults: ContentCalendarMatchedResults | null;
    futureCalendarSummary?: NextFutureCalendarResult | null;
    isLoading: boolean;
    customerId: string;
    isGeneratingPosts: boolean;
    isRequestingDespiteLackingVariety?: boolean;
    generationStatus: CalendarGenerationStatus;
    includeDeleted?: boolean;
    processingSummaries?: Array<ContentSummaryProcessingItem>;
    isLoadingOlderPosts?: boolean;
    includePastInLoad?: boolean;
    hideTopicsWithoutPosts?: boolean;
    debugPostGenLoadingWeekKey?: string | null;
    /** Set by parent when check-status returns a post with no contentSourceMatches for an AI-assisted created item. */
    missingContentStatusCalendarItemId?: string | null;
  }>(),
  {
    includeDeleted: false,
    processingSummaries: () => [],
    isLoadingOlderPosts: false,
    includePastInLoad: false,
    hideTopicsWithoutPosts: true,
    debugPostGenLoadingWeekKey: null,
    isRequestingDespiteLackingVariety: false,
    missingContentStatusCalendarItemId: null,
  }
);

const inputInfo = ref<CalendarInputInfo | null>(null);
const isLoadingInputInfo = ref<boolean>(false);
const inputInfoLoadError = ref<string | null>(null);

const emit = defineEmits<{
  (e: "postsUpdated", post: Array<SocialMediaPostData>, newWeekKey?: string): void;
  (e: "postsDeleted", post: Array<string>): void;
  (e: "calendarItemUpdated", calendarItem: ContentCalendarItem): void;
  (e: "calendarItemsCreated", primaryGroup: CalendarPrimaryGroupName, calendarItems: ContentCalendarItem[]): void;
  (e: "manualPostCreated", primaryGroup: CalendarPrimaryGroupName, weekGroup: string, newItemAndPost: ContentCalendarItemMatch): void;
  (e: "contentMatchBound", contentSummary: ContentSummaryPreview, bucketPath: MatchedResultObjBucketPath): void;
  (e: "bucketDeleted", bucketPath: MatchedResultObjBucketPath): void;
  (e: "weekGuidanceUpdated", primaryGroup: CalendarPrimaryGroupName, weekKey: string, userGuidance: string | null): void;
  (e: "showFilteredTopics"): void;
  (e: "showDeleted"): void;
  (e: "showOlderPosts"): void;
  (e: "triggerWeekPostGen", weekKey: string): void;
  (e: "requestGenerateDespiteLackingVariety"): void;
  (e: "missingContentStatusHandled"): void;
  (e: "pendingChannelsApplied"): void;
}>();

async function loadInputInfo() {
  if (isLoadingInputInfo.value) {
    return;
  }

  isLoadingInputInfo.value = true;
  inputInfoLoadError.value = null;
  inputInfo.value = await ContentCalendarRepo.getInputInfo(resolvedCustomerId.value);
  isLoadingInputInfo.value = false;
}

const isLackingContentVarietyNoticeVisible = computed(() => props.matchedResults?.currentOperation === SocialOperationType.LackingContentVariety);

const shouldShowOlderPostsLink = computed(() => (props.matchedResults?.hasPastPosts ?? false) || (props.matchedResults?.[CalendarPrimaryGroupName.Past]?.weeks?.length ?? 0) > 0);

function handleShowOlderPostsClick() {
  isPreviousPostsSectionVisible.value = true;
  if (!props.includePastInLoad) {
    emit("showOlderPosts");
  }
}

// Weekly guidance ===========================================================
const guidanceDrafts = ref<Record<string, string>>({});
const savingWeekKeys = ref<Set<string>>(new Set());
const expandedGuidanceWeekKey = ref<string | null>(null);

function shouldShowWeekGuidance(weekKey: string): boolean {
  const cutoff = props.matchedResults?.nextPostGenForWeek;
  if (!cutoff) return true;
  return weekKey >= cutoff;
}

function openWeekGuidanceEditor(weekKey: string) {
  expandedGuidanceWeekKey.value = weekKey;
}

function getGuidanceDisplay(weekGroup: { weekGroup: string; userGuidance?: string | null }): string {
  const key = weekGroup.weekGroup;
  if (key in guidanceDrafts.value) return guidanceDrafts.value[key];
  return weekGroup.userGuidance ?? "";
}

function onGuidanceInput(weekGroup: { weekGroup: string }, value: string) {
  guidanceDrafts.value = { ...guidanceDrafts.value, [weekGroup.weekGroup]: value };
}

async function saveWeekGuidance(weekGroup: { weekGroup: string; userGuidance?: string | null }, primaryGroup: { name: CalendarPrimaryGroupName }) {
  const weekKey = weekGroup.weekGroup;
  const value = (getGuidanceDisplay(weekGroup)?.trim() ?? "") || null;
  const original = (weekGroup.userGuidance ?? "").trim() || null;
  if (value === original) return;

  if (savingWeekKeys.value.has(weekKey)) return;
  savingWeekKeys.value = new Set(savingWeekKeys.value).add(weekKey);
  try {
    const result = await ContentCalendarRepo.saveWeekGuidance(props.customerId, weekKey, value);
    const savedGuidance = result?.excludeFromIndex?.userGuidance ?? value;
    emit("weekGuidanceUpdated", primaryGroup.name, weekKey, savedGuidance);
    guidanceDrafts.value = { ...guidanceDrafts.value };
    delete guidanceDrafts.value[weekKey];
  } finally {
    const next = new Set(savingWeekKeys.value);
    next.delete(weekKey);
    savingWeekKeys.value = next;
  }
}

// Generation status helpers ==================================================
const isGenerating = computed<boolean>(() => props.generationStatus !== CalendarGenerationStatus.None);

function getContentMatches(bucket: ContentCalendarItemMatch | null | undefined): Array<ContentSummaryPreview> {
  if (!bucket) return [];
  return bucket.loadedContentMatches ?? [];
}

function formatContentSourceName(bucket: ContentCalendarItemMatch | null | undefined): string | null {
  const loadedNames = getContentMatches(bucket)
    .map(match => match.contextName)
    .filter(Boolean) as Array<string>;
  if (loadedNames.length) {
    if (loadedNames.length <= 2) return loadedNames.join(", ");
    return `${loadedNames.slice(0, 2).join(", ")} (+${loadedNames.length - 2} more)`;
  }

  const fallbackNames = bucket?.calendarItem?.contentSourceMatches?.map(match => match.contextName).filter(Boolean) as Array<string> | undefined;
  if (!fallbackNames?.length) return null;
  if (fallbackNames.length <= 2) return fallbackNames.join(", ");
  return `${fallbackNames.slice(0, 2).join(", ")} (+${fallbackNames.length - 2} more)`;
}

function getGenerationTitle(): string {
  switch (props.generationStatus) {
    case CalendarGenerationStatus.GeneratingCalendar:
      return "Generating Calendar";
    case CalendarGenerationStatus.GeneratingContentSummaries:
      return "Processing Your Uploaded Content";
    default:
      return "Loading Posts";
  }
}

function getGenerationSubtext(): string {
  switch (props.generationStatus) {
    case CalendarGenerationStatus.GeneratingCalendar:
      return "We're generating your social posts!<br>This usually takes a minute or two.";
    case CalendarGenerationStatus.GeneratingContentSummaries:
      return "This may take a few minutes.<br>We'll notify you when it's complete.";
    default:
      return "";
  }
}

// Toggle the View All Photos modal ===========================================
const isViewAllPhotosModalVisible = ref<boolean>(false);
const viewAllPhotosSummaryId = ref<string | null>(null);

// Handle content upload for an Item ==========================================
const isContentUploadModalVisible = ref<boolean>(false);
const calendarItemToUploadContentFor = ref<ContentCalendarItem | null>(null);

// DOM ref's ==================================================================
const domRefRoot = ref<any>(null);

// Toggle Delete confirmation modal ===========================================
const itemToDeleteId = ref<string>("");
const itemToDeleteTitle = ref<string>("");

const isConfirmItemDelectionModalVisible = ref<boolean>(false);

// Approve all posts ==========================================================
const isApprovingAllPosts = ref<boolean>(false);
const isAllPostsScheduleWarningVisible = ref<boolean>(false);

async function approveAllPosts() {
  // Update: This ignore any unused posts in the past, so no need to validate that they're all in the future.

  // Check for missing accounts on any eligible post
  const anyEligiblePostMissingAccounts = props.matchedResults[CalendarPrimaryGroupName.Upcoming].weeks
    .map(w => w.items)
    .flat()
    .map(i => i.socialPost)
    .filter(p => p && isPostEligibleForApproval(p))
    .some(p => !p.postToAccounts || p.postToAccounts?.length === 0);

  if (anyEligiblePostMissingAccounts) {
    if (hasAvailableSocialChannels.value) {
      isApproveAllSelectAccountsModalVisible.value = true;
      MetricSender.sendMetric("Cust-Calendar-Post-ApproveAll-SelectAccountsNeeded", {});
    } else {
      isSocialAccountConnectPrepVisible.value = true;
      MetricSender.sendMetric("Cust-Calendar-Post-ApproveAll-MissingAccountsWarning", {});
    }
    return;
  }

  // Check for team member restriction
  if (shouldBlockTeamMemberApproval()) {
    isTeamMemberApprovalBlockedModalVisible.value = true;
    return;
  }

  isApprovingAllPosts.value = true;
  const updatedPostList = await SocialMediaApproveAndEditRepo.approveAll(resolvedCustomerId.value);
  isApprovingAllPosts.value = false;

  emit("postsUpdated", updatedPostList);

  MetricSender.sendMetric("Cust-Calendar-Post-ApproveAll", {});
}

// Define is some Scheduled posts are not approved or published ===============
const isSomeScheduledPostsEligibleForApproval = computed<boolean>(() => {
  return props.matchedResults?.[CalendarPrimaryGroupName.Upcoming].weeks
    .map(week => week.items)
    .flat()
    .some(bucket => {
      if (!bucket.socialPost) return false;
      return isPostEligibleForApproval(bucket.socialPost) && !tooLateToApprovePost(bucket.socialPost);
    });
});

// Previous Posts visibility ===================================
const isPreviousPostsSectionVisible = ref<boolean>(false);
const showOlderPublished = ref<boolean>(false);
const DEFAULT_PREVIOUS_WEEKS_TO_SHOW = 2;

// Future Posts visibility =====================================
// Hide upcoming weeks that fall more than N weeks past the next post generation week.
// Note: The server has similar config which decides how many weeks in the future to return.
const showFutureWeeks = ref<boolean>(false);
const FUTURE_WEEKS_AFTER_NEXT_GEN = 4;
const MIN_UPCOMING_WEEKS_TO_SHOW = 4;

function getThisMondayWeekKey(): string {
  const today = DayjsExtended().startOf("day");
  const daysFromMonday = (today.day() + 6) % 7;
  return today.subtract(daysFromMonday, "day").format("YYYY-MM-DD");
}

const minimumUpcomingWeekKeys = computed<Array<string>>(() => {
  const thisMonday = DayjsExtended(getThisMondayWeekKey());
  return Array.from({ length: MIN_UPCOMING_WEEKS_TO_SHOW }, (_, index) => thisMonday.add(index, "week").format("YYYY-MM-DD"));
});

function createEmptyWeek(weekKey: string): ContentCalendarWeekMatch {
  return {
    weekGroup: weekKey,
    weekGroupName: DayjsExtended(weekKey).format("MMMM Do"),
    userGuidance: null,
    systemTopics: null,
    items: [],
  };
}

const upcomingWeeksWithMinimum = computed<Array<ContentCalendarWeekMatch>>(() => {
  const weeksByKey = new Map<string, ContentCalendarWeekMatch>();
  (props.matchedResults?.[CalendarPrimaryGroupName.Upcoming]?.weeks ?? []).forEach(week => {
    if (week?.weekGroup) weeksByKey.set(week.weekGroup, week);
  });

  minimumUpcomingWeekKeys.value.forEach(weekKey => {
    if (!weeksByKey.has(weekKey)) weeksByKey.set(weekKey, createEmptyWeek(weekKey));
  });

  return Array.from(weeksByKey.values()).sort((a, b) => a.weekGroup.localeCompare(b.weekGroup));
});

function isWeekHiddenByDefault(weekKey: string): boolean {
  // Always keep the current/near-future empty weeks visible so the calendar never collapses to only "Show More".
  if (minimumUpcomingWeekKeys.value.includes(weekKey)) return false;

  const cutoff = props.matchedResults?.nextPostGenForWeek;
  if (!cutoff) return false;
  const cutoffDate = DayjsExtended(cutoff).add(FUTURE_WEEKS_AFTER_NEXT_GEN, "week");
  if (!cutoffDate.isValid()) return false;
  const weekDate = DayjsExtended(weekKey);
  if (!weekDate.isValid()) return false;
  return weekDate.isAfter(cutoffDate, "day");
}

function getVisibleWeeks(primaryGroup: { name: CalendarPrimaryGroupName; weeks?: Array<ContentCalendarWeekMatch> }): Array<ContentCalendarWeekMatch> {
  const weeks = primaryGroup.weeks ?? [];
  if (primaryGroup.name === CalendarPrimaryGroupName.Past) {
    if (showOlderPublished.value) return weeks;
    return weeks.length > 0 ? weeks.slice(-DEFAULT_PREVIOUS_WEEKS_TO_SHOW) : [];
  }
  if (primaryGroup.name === CalendarPrimaryGroupName.Upcoming && !showFutureWeeks.value) {
    return weeks.filter(w => !isWeekHiddenByDefault(w.weekGroup));
  }
  return weeks;
}

const hasHiddenFutureWeeks = computed<boolean>(() => {
  if (showFutureWeeks.value) return false;
  return upcomingWeeksWithMinimum.value.some(w => isWeekHiddenByDefault(w.weekGroup));
});

const autoPostTaggingAnnouncementModalRef = ref<{ open: () => void } | null>(null);

function openAutoTaggingAnnouncement() {
  autoPostTaggingAnnouncementModalRef.value?.open();
}

const isNewPostModalVisible = ref<boolean>(false);
const primaryGroupNewCalendarItemToAddTo = ref<CalendarPrimaryGroupName | null>(null);
const weekGroupNewCalendarItemToAddTo = ref<string | null>(null);

const isMissingContentFromStatusModalVisible = ref<boolean>(false);
const missingContentReviewOptionsItemId = ref<string | null>(null);
const missingContentModalContext = ref<{
  primaryGroup: CalendarPrimaryGroupName;
  weekGroup: string;
  calendarItem: ContentCalendarItem;
} | null>(null);

const newPostInitialMode = ref<"ai" | "manual" | null>(null);
const newPostInitialTitle = ref<string | null>(null);
const newPostInitialDescription = ref<string | null>(null);
const newPostInitialCaption = ref<string | null>(null);
const newPostInitialPostType = ref<SocialPostType | null>(null);
const newPostInitialCreateManyPosts = ref<boolean>(false);

function openReviewOptionsFromBucket(bucket: ContentCalendarItemMatch, primaryGroup: CalendarPrimaryGroupName) {
  missingContentModalContext.value = {
    primaryGroup,
    weekGroup: bucket.calendarItem.weekGroup,
    calendarItem: bucket.calendarItem,
  };
  missingContentReviewOptionsItemId.value = bucket.calendarItem.id;
  isMissingContentFromStatusModalVisible.value = true;
}

const SUPERUSER_TEST_NON_MATCH_ITEM_ID = "superuser-test-non-match";

function isoWeekGroupKey(d: ReturnType<typeof DayjsExtended>): string {
  return `${d.isoWeekYear()}-W${String(d.isoWeek()).padStart(2, "0")}`;
}

function getSuperuserTestNonMatchPlacement(): { primaryGroup: CalendarPrimaryGroupName; weekGroup: string } {
  const upcomingWeeks = props.matchedResults?.[CalendarPrimaryGroupName.Upcoming]?.weeks;
  const firstWeek = upcomingWeeks?.[0]?.weekGroup;
  if (firstWeek) {
    return { primaryGroup: CalendarPrimaryGroupName.Upcoming, weekGroup: firstWeek };
  }
  return { primaryGroup: CalendarPrimaryGroupName.Upcoming, weekGroup: isoWeekGroupKey(DayjsExtended()) };
}

function buildSuperuserTestNonMatchCalendarItem(weekGroup: string): ContentCalendarItem {
  const now = new Date().toISOString();
  return {
    entityType: "ContentCalendarItem",
    id: SUPERUSER_TEST_NON_MATCH_ITEM_ID,
    matchRationale: "",
    genStatus: CalendarItemGenStatus.Draft,
    customerId: props.customerId,
    title: "Superuser non-match preview topic",
    description: "Fake calendar item: no social post, Draft status, and contentSourceMatches null — same shape as Atlas failing to find a content match for a topic.",
    weekGroup,
    order: 0,
    contentSourceMatches: null,
    createdDate: now,
    createdBy: "superuser-test",
    updatedDate: null,
    updatedBy: null,
  };
}

/** Opens the “no content match” modal with synthetic data (superuser panel only). */
function openSuperuserTestNonMatchModal() {
  const { primaryGroup, weekGroup } = getSuperuserTestNonMatchPlacement();
  missingContentReviewOptionsItemId.value = null;
  missingContentModalContext.value = {
    primaryGroup,
    weekGroup,
    calendarItem: buildSuperuserTestNonMatchCalendarItem(weekGroup),
  };
  isMissingContentFromStatusModalVisible.value = true;
}

function onMissingContentAdjustTheme() {
  const ctx = missingContentModalContext.value;
  if (!ctx) {
    return;
  }
  newPostInitialMode.value = "ai";
  newPostInitialTitle.value = ctx.calendarItem.title ?? "";
  newPostInitialDescription.value = ctx.calendarItem.description ?? "";
  newPostInitialCaption.value = null;
  primaryGroupNewCalendarItemToAddTo.value = ctx.primaryGroup;
  weekGroupNewCalendarItemToAddTo.value = ctx.weekGroup;
  isNewPostModalVisible.value = true;
}

function onMissingContentManualDraft() {
  const ctx = missingContentModalContext.value;
  if (!ctx) {
    return;
  }
  newPostInitialMode.value = "manual";
  newPostInitialTitle.value = null;
  newPostInitialDescription.value = null;
  const t = ctx.calendarItem.title?.trim();
  const d = ctx.calendarItem.description?.trim();
  newPostInitialCaption.value = [t, d].filter(Boolean).join("\n") || "";
  primaryGroupNewCalendarItemToAddTo.value = ctx.primaryGroup;
  weekGroupNewCalendarItemToAddTo.value = ctx.weekGroup;
  isNewPostModalVisible.value = true;
}

// When a manual post doesn't match we show a special modal to help them proceed.
watch(
  () => props.missingContentStatusCalendarItemId,
  async id => {
    if (!id || !props.matchedResults) {
      return;
    }
    await nextTick();
    const path = ContentCalendarHelper.findBucketPath(props.matchedResults, id);
    if (path.primaryGroup === undefined || path.weekIndex === undefined || path.bucketIndex === undefined) {
      emit("missingContentStatusHandled");
      return;
    }
    const bucket = props.matchedResults[path.primaryGroup].weeks[path.weekIndex].items[path.bucketIndex];
    if (!bucket?.calendarItem) {
      emit("missingContentStatusHandled");
      return;
    }
    if (bucket.calendarItem.contentSourceMatches?.length) {
      emit("missingContentStatusHandled");
      return;
    }
    const hasPost = Boolean(bucket.socialPost);
    const st = bucket.calendarItem.genStatus;
    const okToShow = hasPost || st === CalendarItemGenStatus.Draft || st === CalendarItemGenStatus.Failed;
    if (!okToShow) {
      emit("missingContentStatusHandled");
      return;
    }
    missingContentReviewOptionsItemId.value = id;
    missingContentModalContext.value = {
      primaryGroup: path.primaryGroup,
      weekGroup: bucket.calendarItem.weekGroup,
      calendarItem: bucket.calendarItem,
    };
    isMissingContentFromStatusModalVisible.value = true;
    emit("missingContentStatusHandled");
  }
);

function handleCalendarItemsCreated(createdItems: ContentCalendarItem[]) {
  if (!createdItems.length) {
    return;
  }
  if (createdItems.some(item => isWeekHiddenByDefault(item.weekGroup))) {
    showFutureWeeks.value = true;
  }
  emit("calendarItemsCreated", primaryGroupNewCalendarItemToAddTo.value ?? CalendarPrimaryGroupName.Upcoming, createdItems);
}

function openNewPostModal(primaryGroupName: CalendarPrimaryGroupName, weekGroupKey: string, postType?: SocialPostType, createManyPosts = false) {
  newPostInitialMode.value = null;
  newPostInitialTitle.value = null;
  newPostInitialDescription.value = null;
  newPostInitialCaption.value = null;
  newPostInitialPostType.value = postType === SocialPostType.Reel || postType === SocialPostType.Story ? postType : null;
  newPostInitialCreateManyPosts.value = createManyPosts;
  primaryGroupNewCalendarItemToAddTo.value = primaryGroupName;
  weekGroupNewCalendarItemToAddTo.value = weekGroupKey;
  isNewPostModalVisible.value = true;
}

function openTopLevelCreatePosts() {
  const nextWeekKey = DayjsExtended(getThisMondayWeekKey()).add(1, "week").format("YYYY-MM-DD");
  openNewPostModal(CalendarPrimaryGroupName.Upcoming, nextWeekKey, undefined, true);
}

// Use existing content =======================================================
const pickFromExistingContentItem = ref<ContentCalendarItem | null>(null);
const isPickFromExistingContentModalVisible = ref<boolean>(false);

// Toggle post visibility =====================================================
const postIdsThatVisible = ref<Array<string>>([]);
const knownPostIds = ref<Array<string>>([]);

function bucketHasContentMatches(bucket: ContentCalendarItemMatch | null | undefined): boolean {
  return (bucket?.calendarItem?.contentSourceMatches?.length ?? 0) > 0;
}

/** Topic-only rows (no social post) are hidden unless post generation is still in flight. */
function shouldShowCalendarItemBucket(bucket: ContentCalendarItemMatch): boolean {
  if (isDebugMode.value) {
    return true;
  }
  if (!props.hideTopicsWithoutPosts) {
    return true;
  }
  if (bucket.socialPost) {
    return true;
  }
  const st = bucket.calendarItem.genStatus;
  return st === CalendarItemGenStatus.Pending || st === CalendarItemGenStatus.Generating;
}

function showsMissingContentEmptyState(bucket: ContentCalendarItemMatch): boolean {
  if (bucket.isLoadingContentMatches) {
    return false;
  }
  if (getContentMatches(bucket).length > 0) {
    return false;
  }
  if (bucket.socialPost) {
    return true;
  }
  return !bucketHasContentMatches(bucket);
}

async function ensureContentMatchesLoaded(bucket: ContentCalendarItemMatch) {
  if (!bucketHasContentMatches(bucket) || bucket.loadedContentMatches !== undefined || bucket.isLoadingContentMatches) {
    return;
  }

  bucket.isLoadingContentMatches = true;
  try {
    bucket.loadedContentMatches = await ContentCalendarRepo.getContentSummaryPreviewsForItem(props.customerId, bucket.calendarItem.id);
  } finally {
    bucket.isLoadingContentMatches = false;
  }
}

async function togglePostVisibility(bucket: ContentCalendarItemMatch) {
  const postId = bucket.socialPost?.id;
  if (!postId) return;

  if (postIdsThatVisible.value.includes(postId)) {
    postIdsThatVisible.value = postIdsThatVisible.value.filter(id => id !== postId);
    await ensureContentMatchesLoaded(bucket);
    return;
  }

  postIdsThatVisible.value.push(postId);
}

function setPostsVisibility() {
  const nextPostIds = props.flatBucketList.map(bucket => bucket.socialPost?.id).filter(item => Boolean(item)) as Array<string>;
  if (!knownPostIds.value.length && !postIdsThatVisible.value.length) {
    postIdsThatVisible.value = nextPostIds;
    knownPostIds.value = nextPostIds;
    return;
  }

  const currentlyVisible = new Set(postIdsThatVisible.value);
  const previouslyKnown = new Set(knownPostIds.value);
  postIdsThatVisible.value = nextPostIds.filter(id => currentlyVisible.has(id) || !previouslyKnown.has(id));
  knownPostIds.value = nextPostIds;
}

watch(() => props.flatBucketList, setPostsVisibility, { deep: true });

watch(resolvedCustomerId, () => {
  inputInfo.value = null;
  inputInfoLoadError.value = null;
  isLoadingInputInfo.value = false;
});

onMounted(setPostsVisibility);

// Scroll to Upcoming Posts if hash contains reviewAndApprove ==================
watch(
  () => props.isLoading,
  async isLoading => {
    if (!isLoading && route.hash.includes("reviewAndApprove")) {
      await nextTick();
      setTimeout(() => {
        document.querySelector('[data-section-title="upcoming-posts"]')?.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
        history.replaceState(null, "", window.location.pathname + window.location.search);
      }, 100);
    }
  }
);

// Toggle Post Edit and Share modal ===========================================
const isPostEditModalVisible = ref<boolean>(false);
const bucketToEdit = ref<ContentCalendarItemMatch | null>(null);

function openPostEditor(bucket: ContentCalendarItemMatch) {
  bucketToEdit.value = bucket;
  isPostEditModalVisible.value = true;
}

function canEditPostFromPreview(post: SocialMediaPostData): boolean {
  return !post.postedDetails?.length && !tooLateToApprovePost(post);
}

function handlePostEditSave(updatedPost: SocialMediaPostData) {
  emit("postsUpdated", [updatedPost]);
}

watch(isPostEditModalVisible, visible => {
  if (!visible) {
    bucketToEdit.value = null;
  }
});

// Load social settings =======================================================
const systemSettings = ref<ContentCalendarSystemSettingsData | null>(null);
const userSettings = ref<ContentCalendarUserSettingsData | null>(null);
const customerTimeZone = ref<string | null>(null);
const calendarImageFit = computed<SocialImageFitSettings | null>(() => userSettings.value?.imageFit ?? null);
const customersSocialChannelsForPicker = computed(() => {
  const list = systemSettings.value?.socialChannels || [];
  return list.filter(s => s.status !== SocialChannelStatus.Deleted);
});
const hasAvailableSocialChannels = computed<boolean>(() => customersSocialChannelsForPicker.value.length > 0);
const connectedPlatformChannelTypes = computed(() => customersSocialChannelsForPicker.value.filter(s => s.status === SocialChannelStatus.Connected).map(s => s.channelType));
const hasAuthNeededSocialChannel = computed(() => {
  const list = systemSettings.value?.socialChannels || [];
  return list.some(ch => ch.status !== SocialChannelStatus.Deleted && isChannelAuthNeeded(ch.status));
});

// Connect account (prep modal) ================================================
const isSocialAccountConnectPrepVisible = ref<boolean>(false);
const isRedirectingToPortal = ref<boolean>(false);

// Approve flow: pick accounts when post has none but customer has channels =====
const postForApprovalNetworkPicker = ref<SocialMediaPostData | null>(null);
const isApprovalNetworkPickerVisible = ref<boolean>(false);
const isApproveAllSelectAccountsModalVisible = ref<boolean>(false);

watch(isApprovalNetworkPickerVisible, isVisible => {
  if (!isVisible) postForApprovalNetworkPicker.value = null;
});

async function handleApprovalNetworkPickerSave(selectedSocialChannels: Array<SocialChannels>) {
  const post = postForApprovalNetworkPicker.value;
  if (!post) return;
  postForApprovalNetworkPicker.value = null;
  isApprovalNetworkPickerVisible.value = false;
  emit("postsUpdated", [{ ...post, postToAccounts: selectedSocialChannels }]);
  await approvePost({ ...post, postToAccounts: selectedSocialChannels });
}

// Team member approval blocked modal ==========================================
const isTeamMemberApprovalBlockedModalVisible = ref<boolean>(false);

async function linkMoreAccounts() {
  isSocialAccountConnectPrepVisible.value = true;
}

async function confirmLinkMoreAccounts(payload: { selectedPlatforms: Array<string>; forceStandardScope?: boolean }) {
  if (isRedirectingToPortal.value) return;
  isRedirectingToPortal.value = true;
  await ContentCalendarHelper.linkMoreAccounts(props.customerId, "posts", payload.selectedPlatforms, {
    forceStandardScope: payload.forceStandardScope === true,
  });
}

async function loadSocialSettings() {
  const v2Response = await SocialMediaSettingsRepo.getSettings(resolvedCustomerId.value);
  systemSettings.value = v2Response?.settings ?? null;
  userSettings.value = v2Response?.userSettings ?? null;
  customerTimeZone.value = v2Response?.timeZone ?? null;
}

// Reload settings after posts finish so #sync PendingChannels is not missed.
watch(
  () => props.isLoading,
  async isLoading => {
    if (isLoading) return;
    await loadSocialSettings();
  },
  { immediate: true }
);

function handlePendingChannelsApplied(response: ApplyPendingChannelsResponse) {
  systemSettings.value = response.settings ?? null;
  userSettings.value = response.userSettings ?? userSettings.value;
  emit("pendingChannelsApplied");
}

// Approve/unapprove social post ==============================================
const postIdsThatBeingUpdated = ref<Array<string>>([]);

function tooLateToApprovePost(post: SocialMediaPostData): boolean {
  if (!post?.scheduledToPostAt) return false;
  return isScheduledDateTooSoon(post.scheduledToPostAt.localDateTime, post.scheduledToPostAt.timeZone);
}

function isPostUnused(post: SocialMediaPostData): boolean {
  if (post?.status === SocialPostStatus.Approved || post?.status === SocialPostStatus.Published) {
    return false;
  }
  return tooLateToApprovePost(post);
}

async function approvePost(post: SocialMediaPostData) {
  if (!post?.scheduledToPostAt) {
    remoteLogger.error(`Can't approve post ${post.id}, scheduledToPostAt is null`);
    return;
  }
  if (isScheduledDateTooSoon(post.scheduledToPostAt.localDateTime, post.scheduledToPostAt.timeZone)) {
    await togglePostScheduledTimeWarning(post.id);
    MetricSender.sendMetric("Cust-Calendar-Post-Approve-ScheduleWarning", { socialPostId: post.id });
    return;
  }

  if (!post.postToAccounts || post.postToAccounts?.length === 0) {
    if (hasAvailableSocialChannels.value) {
      postForApprovalNetworkPicker.value = post;
      isApprovalNetworkPickerVisible.value = true;
      MetricSender.sendMetric("Cust-Calendar-Post-Approve-SelectAccounts", { socialPostId: post.id });
    } else {
      isSocialAccountConnectPrepVisible.value = true;
      MetricSender.sendMetric("Cust-Calendar-Post-Approve-MissingAccountsWarning", { socialPostId: post.id });
    }
    return;
  }
  // Check for team member restriction
  if (shouldBlockTeamMemberApproval()) {
    isTeamMemberApprovalBlockedModalVisible.value = true;
    return;
  }

  postIdsThatBeingUpdated.value.push(post.id);
  const updatedPost = await SocialMediaApproveAndEditRepo.approveSocialPost(resolvedCustomerId.value, post.id);
  postIdsThatBeingUpdated.value = postIdsThatBeingUpdated.value.filter(id => id !== post.id);

  if (updatedPost) {
    emit("postsUpdated", [updatedPost]);
  }

  MetricSender.sendMetric("Cust-Calendar-Post-Approve", { socialPostId: post.id });
}

const postIdsToShowScheduledDateWarning = ref<Array<string>>([]);

async function togglePostScheduledTimeWarning(postId: string) {
  postIdsToShowScheduledDateWarning.value.push(postId);

  await new Promise(resolve => setTimeout(resolve, MS_TO_SHOW_WARNING));
  postIdsToShowScheduledDateWarning.value = postIdsToShowScheduledDateWarning.value.filter(id => id !== postId);
}

async function unapprovePost(postId: string) {
  postIdsThatBeingUpdated.value.push(postId);
  const updatedPost = await SocialMediaApproveAndEditRepo.unapproveSocialPost(resolvedCustomerId.value, postId);
  postIdsThatBeingUpdated.value = postIdsThatBeingUpdated.value.filter(id => id !== postId);

  if (updatedPost) {
    emit("postsUpdated", [updatedPost]);
  }

  MetricSender.sendMetric("Cust-Calendar-Post-Unapprove", { socialPostId: postId });
}

// Handle post rating =========================================================
async function handleRatePost(postId: string, rating: number) {
  MetricSender.sendMetric("Cust-Calendar-Post-Rate", { socialPostId: postId, rating });

  const updatedPost = await SocialMediaApproveAndEditRepo.ratePost(resolvedCustomerId.value, postId, rating);

  if (updatedPost) {
    emit("postsUpdated", [updatedPost]);
  }
}

// Debug mode =================================================================
const isDebugMode = ref<boolean>(false);
const areEvalsVisible = ref<boolean>(false);

// Show test calendars ========================================================
const showTestCalendars = ref<boolean>(getShowTestCalendars());

function toggleShowTestCalendars() {
  const newValue = !showTestCalendars.value;
  setShowTestCalendars(newValue);
  window.location.reload();
}

// Future Calendar Sections =======================================================
const isFutureCalendarTestVisible = ref<boolean>(false);

function openConfigAdminForPost(postId: string) {
  const routeLocation = router.resolve({ name: "ConfigAdmin", query: { customerId: resolvedCustomerId.value, postId }, hash: "#social-post-from-just-images" });
  const href = routeLocation.href.startsWith("http") ? routeLocation.href : `${window.location.origin}${routeLocation.href}`;
  window.open(href, "_blank", "noopener,noreferrer");
}

async function openCalendarGenPrompt() {
  const calendarId = await getCalendarId(resolvedCustomerId.value);
  const routeLocation = router.resolve({ name: "ConfigAdmin", query: { customerId: resolvedCustomerId.value, calendarId: calendarId, calendarGen: "true" }, hash: "#cal-generate" });
  const href = routeLocation.href.startsWith("http") ? routeLocation.href : `${window.location.origin}${routeLocation.href}`;
  window.open(href, "_blank", "noopener,noreferrer");
}

async function openCalendarMatchPrompt() {
  const calendarId = await getCalendarId(resolvedCustomerId.value);
  const routeLocation = router.resolve({ name: "ConfigAdmin", query: { customerId: resolvedCustomerId.value, calendarId: calendarId, calendarMatch: "true" }, hash: "#cal-content-match" });
  const href = routeLocation.href.startsWith("http") ? routeLocation.href : `${window.location.origin}${routeLocation.href}`;
  window.open(href, "_blank", "noopener,noreferrer");
}

// Debug info =================================================================
const allDebugInfo = ref<Array<ImageSelectAIDebugData> | null>(null);
async function toggleDebugInfo() {
  isDebugMode.value = !isDebugMode.value;
  if (allDebugInfo.value === null) {
    allDebugInfo.value = await ShareStopSummaryRepo.getDebugInfo(resolvedCustomerId.value);
  }
}

function findDebugInfo(postId: string): ImageSelectAIDebugData | null {
  if (!isDebugMode.value) return null;
  return allDebugInfo.value?.find(debug => debug.sourceEntityId === postId) || null;
}

// ============================================================================
const itemsWithStatusGenerating = computed<Array<ContentCalendarItemMatch>>(() => {
  if (!props.matchedResults) {
    return [];
  }
  return props.matchedResults[CalendarPrimaryGroupName.Upcoming].weeks
    .map(weekGroup => weekGroup.items)
    .flat()
    .filter(bucket => bucket.calendarItem.genStatus === CalendarItemGenStatus.Generating);
});

// Post dislike logic ==============================================
const isPostDislikeModalVisible = ref<boolean>(false);
const bucketToDislike = ref<ContentCalendarItemMatch | null>(null);
const isRemovingPost = ref<boolean>(false);

async function dislikeAndRemovePost(reasonText: string, customerId: string, postId: string) {
  isRemovingPost.value = true;

  await ShareStopSummaryRepo.deletePost(props.customerId, postId, reasonText);
  emit("postsDeleted", [postId]);
  // We also delete the calendar item now
  emit("bucketDeleted", ContentCalendarHelper.findBucketPath(props.matchedResults, bucketToDislike.value.calendarItem.id));
  isRemovingPost.value = false;

  MetricSender.sendMetric("Cust-Calendar-Post-Delete", { socialPostId: postId });
}

// Sort items by scheduled time, else specificDate, else unused order =========
function sortItemsByPostAndOrder(itemList: Array<ContentCalendarItemMatch>): Array<ContentCalendarItemMatch> {
  return [...itemList].sort(compareCalendarItemsBySchedule);
}
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";
@import "@/scss/or-divider.scss";
@import "./headers.scss";
@import "@/scss/variables.scss";

// Header main ================================================================
.header-main {
  &__top-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 10px 20px;
    margin-bottom: 10px;
  }

  &__title {
    flex-grow: 1;
    margin-bottom: 0;
  }

  &__title-row {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-grow: 1;
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px 16px;
    flex-shrink: 0;
  }

  &__reauth-wrap {
    flex-shrink: 0;
  }

  &__settings-link {
    flex-shrink: 0;
    white-space: nowrap;
  }
}

// Social post container ======================================================
.social-post-container {
  &__skeleton-wrap {
    position: relative;
    z-index: 0;
  }

  &__skeleton-overlay-text {
    @include standardFont;
    width: 100%;
    height: 100%;
    padding: 12px 16px;
    margin: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    inset: 0 auto auto 0;
    z-index: 1;
    text-align: center;
    background: radial-gradient(ellipse 60% 25% at center, rgba(255, 255, 255, 0.92) 0%, rgba(255, 255, 255, 0.75) 35%, rgba(255, 255, 255, 0) 65%);
  }

  &__skeleton {
  }

  &__post {
  }
}

// Calendar item container ====================================================
.calendar-item-container {
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 0;

  &__in-wrap {
    width: calc(100% + 30px);
    min-width: calc(100% + 30px);
    padding: 0 15px;
    margin: 0 0 0 -15px;
    display: flex;
    flex-direction: column;
  }

  &--highlighted-left {
    &::after {
      content: "";
      width: 3px;
      height: 100%;
      border-radius: 100px;
      position: absolute;
      inset: 0 auto auto -15px;
      background: $brand-color;
    }
  }

  &--highlighted-right {
    &::after {
      content: "";
      width: 3px;
      height: 100%;
      border-radius: 100px;
      position: absolute;
      inset: 0 -15px auto auto;
      background: $brand-color;
    }
  }

  &__social-post-switch {
    order: 3;
    margin-right: 10px;
  }

  &__social-post-switch-row {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 6px;
    order: 3;
  }

  &__social-post-container {
    margin-bottom: 8px;
  }

  &__calendar-item-wrap {
  }

  &__calendar-item {
    position: relative;
    z-index: 1;
  }

  &__content-match-snippet {
  }
}

// Week group =================================================================
.week-group {
  position: relative;
  z-index: 0;

  &--highlighted {
    &::before {
      content: "";
      width: calc(100% + 20px);
      height: calc(100% + 10px);
      border: 1px rgba(17, 134, 137, 0.4) solid;
      border-radius: 7px;
      position: absolute;
      inset: -5px auto auto -10px;
      z-index: 10;
      background: rgba(17, 134, 137, 0.2);
      pointer-events: none;
    }
  }

  &--highlighted.week-group--dimmed {
    &::before {
      content: "";
      width: calc(100% + 20px);
      height: calc(100% + 10px);
      border: 1px rgba(17, 134, 137, 0.8) solid;
      border-radius: 7px;
      position: absolute;
      inset: -5px auto auto -10px;
      z-index: 10;
      background: rgba(17, 134, 137, 0.4);
      pointer-events: none;
    }
  }

  &--dimmed {
    opacity: 0.5;
  }
}

.week-group {
  &__title {
    margin-bottom: 16px;
  }

  &__guidance {
    margin-bottom: 16px;
  }

  &__add-post-inline {
    display: inline-flex;
    margin-bottom: 16px;
  }

  &__calendar-items-list {
    gap: $desktop-wide-grid-gap-width;
    padding: 0;
    margin: 0;
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    list-style: none;
  }

  &__calendar-item-container {
    width: $desktop-wide-3-columns;
    margin-top: 10px;
    margin-bottom: 10px;

    &--centered {
      align-self: center;
      justify-self: center;
      display: flex;
      justify-content: center;
    }

    &--left-aligned {
      display: flex;
      justify-content: flex-start;
    }
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
  .week-group {
    &__calendar-items-list {
      gap: $desktop-grid-gap-width;
    }

    &__calendar-item-container {
      width: $desktop-3-columns;
    }
  }
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
  .week-group {
    &__calendar-items-list {
      gap: $laptop-grid-gap-width;
    }

    &__calendar-item-container {
      width: $laptop-3-columns;
    }
  }
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
  .week-group {
    &__calendar-items-list {
      gap: $tablet-large-grid-gap-width;
    }

    &__calendar-item-container {
      width: calc($tablet-large-content-width / 2 - $tablet-large-grid-gap-width * 1 / 2);
    }
  }
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .week-group {
    &__calendar-items-list {
      gap: $tablet-grid-gap-width;
    }

    &__calendar-item-container {
      width: calc($tablet-content-width / 2 - $tablet-grid-gap-width * 1 / 2);
    }
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .week-group {
    &__calendar-items-list {
      gap: $mobile-grid-gap-width;
    }

    &__calendar-item-container {
      width: $mobile-content-width;
      margin-bottom: 40px;
    }
  }
}

// Edit calendar form =========================================================
.edit-calendar-form {
  @include standardFont;

  &__title {
    margin-bottom: 2px;

    &--disabled {
      opacity: 0.4;
      pointer-events: none;
    }
  }

  &__subtitle {
    margin-bottom: 13px;

    &--disabled {
      opacity: 0.4;
      pointer-events: none;
    }
  }

  &__textarea {
    min-height: 115px;
    margin-bottom: 16px;
  }

  &__action-button {
  }
}

// Edit calendar forms container ==============================================
.edit-calendar-forms-container {
  display: flex;
  align-items: center;

  &--disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  &__left-side {
    width: $desktop-wide-2-columns;
    display: flex;
    align-items: center;
  }

  &__or-divider-wrap {
    width: calc($desktop-wide-grid-col-width + $desktop-wide-grid-gap-width * 2);
    align-self: stretch;
    display: flex;
    justify-content: center;
    align-items: center;

    &--disabled {
      opacity: 0.4;
      pointer-events: none;
    }
  }

  &__or-divider {
    height: 100%;
  }

  &__right-side {
    flex-grow: 1;
    display: flex;
    align-items: center;

    &--disabled {
      opacity: 0.4;
      pointer-events: none;
    }
  }

  &__form {
    flex-grow: 1;
  }
}

// Buttons panel ==============================================================
.buttons-panel {
  display: flex;
  align-items: center;
  justify-content: space-between;

  &--disabled {
    opacity: 0.4;
    pointer-events: none;
  }

  &__left-side {
    display: flex;
    align-items: center;
  }

  &__right-side {
    gap: 20px;
    display: flex;
    align-items: center;
  }

  &__button {
  }
}

// Manage buttons floating panel ==============================================
.manage-buttons-floating-panel {
  gap: 20px;
  display: flex;
  align-items: center;
  box-shadow: 0 -15px 20px 20px rgba(255, 255, 255, 1);
  background: rgba(255, 255, 255, 1);

  &__button {
  }

  &__processing-indicator {
    margin-left: auto;
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .manage-buttons-floating-panel {
    gap: 10px;

    /*&__button {
      height: 65px !important;
      min-height: 65px !important;
    }*/
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .manage-buttons-floating-panel {
    gap: 10px;
    box-shadow: 0 -5px 30px 10px rgba(255, 255, 255, 1);

    /*&__button {
      height: 65px !important;
      min-height: 65px !important;
    }*/
  }
}

// Weekly calendar with matches ===============================================
.weekly-calendar-with-matches {
  &--disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  &__header-main {
    // margin-bottom: 35px;
    border-bottom: 0;
    padding-bottom: 0;
  }

  &__section-header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px 15px;
    margin-bottom: 10px;
  }

  &__section-title {
    margin-bottom: 0;
  }

  &__show-older {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-left: 10px;
  }

  &__create-posts-wrap {
    margin-left: auto;
  }

  &__week-group-list-wrap {
    padding: 0;
    margin: 0 0 65px;
    display: flex;
    flex-direction: column;
    list-style: none;
    position: relative;
    z-index: 0;

    &::before {
      content: "";
      width: calc(100% + 60px);
      height: calc(100% - 40px);
      border: 2px rgba(255, 255, 255, 0) solid;
      border-radius: 6px;
      position: absolute;
      inset: 40px auto auto -30px;
      z-index: -1;
    }

    &--invisible {
      display: none;
    }

    &--no-frame {
      &::before {
        display: none;
      }
    }
    &--gray-frame {
      &::before {
        border-color: rgba(204, 211, 215, 1);
      }
    }
    &--teal-frame {
      &::before {
        border-color: rgba(17, 134, 137, 1);
      }
    }
    &--yellow-frame {
      &::before {
        border-color: rgba(204, 162, 47, 1);
      }
    }
  }

  &__sticky-bottom-border {
    width: calc(100% + 60px);
    height: 4px;
    margin-left: -30px;
    position: sticky;
    inset: auto auto 0 0;
    z-index: 2;
    background: rgba(255, 255, 255, 1);
    pointer-events: none;

    &::before {
      content: "";
      width: 100%;
      height: 8px;
      border: 2px rgba(0, 0, 0, 1) solid;
      border-width: 0 2px 2px 2px;
      border-radius: 0 0 6px 6px;
      position: absolute;
      inset: auto auto 0 0;
    }
  }
  &__week-group-list-wrap--no-frame &__sticky-bottom-border::before {
    display: none;
  }
  &__week-group-list-wrap--gray-frame &__sticky-bottom-border::before {
    border-color: rgba(204, 211, 215, 1);
  }
  &__week-group-list-wrap--teal-frame &__sticky-bottom-border::before {
    border-color: rgba(17, 134, 137, 1);
  }
  &__week-group-list-wrap--yellow-frame &__sticky-bottom-border::before {
    border-color: rgba(204, 162, 47, 1);
  }

  &__week-group-list {
    gap: 27px;
    padding: 30px 0 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    list-style: none;
    position: relative;
    z-index: 0;
  }

  &__generation-message {
    width: calc(100% + 60px);
    padding: 20px 30px;
    margin-left: -30px;
    position: relative;
    z-index: 1;
    background: rgba(255, 255, 255, 1);
  }

  &__week-group {
    margin: 0;

    &--old {
      background: white;
    }
    &--recent {
      background: gray;
    }
    &--scheduled {
      background: teal;
    }
    &--future {
      background: yellow;
    }

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }

    &--hidden {
      display: none;
    }

    &--smaller-vertical-margins {
      margin: 1px 0;
    }
  }

  &__manage-buttons-floating-panel {
    margin-bottom: 25px;
    align-self: flex-start;
    position: sticky;
    inset: auto auto 15px 0;

    &--bigger-bottom-indent-on-mobile-when-sticky {
    }

    &--right-aligned {
      justify-self: flex-end;
    }
  }

  &__divider {
    margin-bottom: 5px;
    border-bottom: 1px rgba(0, 0, 0, 0.15) solid;
  }

  &__edit-calendar-forms-container {
    margin-bottom: 45px;
  }

  &__buttons-panel {
  }

  &__future-content-needs {
    margin-top: 40px;
    margin-bottom: 25px;
  }

  &__variety-notice {
    margin-bottom: 28px;
  }

  &__variety-notice-actions {
    margin-top: 16px;
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
    align-items: center;
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .weekly-calendar-with-matches {
    &__section-title {
      white-space: nowrap;
    }

    &__create-posts-wrap {
      flex: 1 0 100%;
      margin-left: 0;
      display: flex;
      justify-content: flex-end;
    }

    &__week-group-list-wrap {
      &::before {
        width: calc(100% + 20px);
        height: calc(100% - 40px);
        inset: 40px auto auto -10px;
      }

      &--has-create-posts::before {
        height: calc(100% - 76px);
        inset: 76px auto auto -10px;
      }
    }

    &__manage-buttons-floating-panel {
      margin-bottom: 25px;
      inset: auto auto 15px 0;
    }

    &__sticky-bottom-border {
      width: calc(100% + 20px);
      margin-left: -10px;
      inset: auto auto 0 0;
    }
  }
}
</style>
